import { forwardRef } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { Action } from '@hh.ru/magritte-ui-action';
import ChipContent from '@hh.ru/magritte-ui-chips/ChipContent';
import type { CustomChipProps } from '@hh.ru/magritte-ui-chips/types';
import { CrossOutlinedSize24, CrossOutlinedSize16 } from '@hh.ru/magritte-ui-icon/variants/icon';

import styles from './chips.less';

const STYLE_TO_CSS = {
    neutral: styles.chipStyleNeutral,
    accent: styles.chipStyleAccent,
};

const CustomChipComponent: PolymorphicForwardRefRenderFunc<CustomChipProps, 'button'> = (
    {
        Element = 'button',
        addon,
        style = 'neutral',
        postfix,
        onDelete,
        deleteOnly = false,
        checked = false,
        disabled = false,
        'aria-label': ariaLabel,
        hideLabel,
        dataQaWrapper = 'chip',
        children,
        size = 'medium',
        mode = 'primary',
        ...buttonProps
    },
    ref
) => {
    const forceDisabled = useDisabled();
    const isComponentDisabled = disabled || forceDisabled;

    let additionalAttributes: Record<string, unknown>;

    if (Element === 'button') {
        additionalAttributes = {
            type: 'button',
            tabIndex: isComponentDisabled || deleteOnly ? -1 : 0,
        };
    } else {
        additionalAttributes = {
            tabIndex: isComponentDisabled || deleteOnly ? -1 : 0,
            'aria-disabled': disabled,
        };
    }

    if (ref) {
        additionalAttributes.ref = ref;
    }

    return (
        <div
            className={classnames(styles.chipWrapper, isComponentDisabled && styles.chipWrapperDisabled)}
            data-qa={dataQaWrapper}
        >
            <Element
                {...additionalAttributes}
                {...buttonProps}
                className={classnames(styles.chip, styles.chipCustom, STYLE_TO_CSS[style], {
                    [styles.chipChecked]: checked,
                    [styles.deleteOnly]: deleteOnly,
                    [styles.sizeSmall]: size === 'small',
                    [styles.disabled]: isComponentDisabled,
                    [styles.chipSecondary]: mode === 'secondary',
                })}
                disabled={isComponentDisabled || deleteOnly}
            >
                <ChipContent
                    postfix={postfix}
                    onDelete={onDelete}
                    addon={addon}
                    aria-label={ariaLabel}
                    hideLabel={hideLabel}
                    size={size}
                    mode={mode}
                >
                    {children}
                </ChipContent>
            </Element>
            {onDelete && (
                <span className={classnames(styles.close, { [styles.closeSecondary]: mode === 'secondary' })}>
                    <Action
                        data-qa="chip-delete-action"
                        onClick={onDelete}
                        disabled={isComponentDisabled}
                        mode="secondary"
                        icon={size === 'small' || mode === 'secondary' ? CrossOutlinedSize16 : CrossOutlinedSize24}
                    />
                </span>
            )}
        </div>
    );
};

export const CustomChip: PolymorphicComponentWithRef<CustomChipProps, 'button'> = forwardRef(CustomChipComponent);
